import { Component, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { InspectionItemResult } from '../../types/inspection.type';
import { ModalController, IonicModule } from '@ionic/angular';
import { environment } from '../../../environments/environment';

import { TranslateService } from '../../services/translate.service';
import { AppData } from 'src/app/data';
import { CustomField, CustomFieldTypeEnum } from 'src/app/types/custom-field.type';
import { decimalValidator } from 'src/app/validators/decimal.validator';
import { NotificationService } from 'src/app/services';
import { FormErrorComponent } from '../../components/form-error/form-error.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-custom-form',
  templateUrl: './custom-form.page.html',
  styleUrls: ['./custom-form.page.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [IonicModule, FormsModule, ReactiveFormsModule, FormErrorComponent, TranslatePipe],
})
export class CustomFormPage implements OnInit {
  @Input() onComplete: (customFormResults: InspectionItemResult[]) => void;
  @Input() customForm: CustomField[];

  public form: FormGroup = new FormGroup({});

  public readonly data = inject(AppData);
  private readonly modalCtrl = inject(ModalController);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);

  private readonly gtm = (<any>window).dataLayer || [];
  public itemsComplete = 0;
  public features = environment.features;
  public submitting = false;

  public readonly wholeNumberError = this.translateService.translate('VALIDATION.isInteger');

  ngOnInit() {
    this.gtm.push({ event: 'custom_event', custom_event_name: 'view_custom_form' });

    // initialize form group and results array
    this.customForm.forEach(field => {
      let control: FormControl;
      switch (field.type) {
        case CustomFieldTypeEnum.Integer:
          control = new FormControl(null, [
            decimalValidator(0),
            Validators.min(-1_000_000),
            Validators.max(1_000_000),
          ]);
          break;
        case CustomFieldTypeEnum.Decimal:
          control = new FormControl(null, [Validators.min(-1_000_000), Validators.max(1_000_000)]);
          break;
        case CustomFieldTypeEnum.Boolean:
          control = new FormControl(false);
          break;
        case CustomFieldTypeEnum.Text:
        default:
          control = new FormControl('', [Validators.maxLength(255)]);
      }
      this.form.addControl(field.id.toString(), control);
    });
  }

  public async submit(): Promise<void> {
    try {
      this.submitting = true;
      if (this.form.invalid) {
        this.form.markAllAsTouched();
        return;
      }
      const customFormResults = Object.keys(this.form.controls).map(
        key =>
          <InspectionItemResult>{
            inspectionItemId: Number(key),
            result: this.form.controls[key]?.value?.toString() ?? '',
            comment: '',
          },
      );
      this.onComplete(customFormResults);
      await this.modalCtrl.dismiss({ customFormResults }, 'submit', 'customFields');
    } catch (err) {
      this.notificationService.error(err?.message || err);
    } finally {
      this.submitting = false;
    }
  }

  public cancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel', 'customFields');
  }
}
